import {
  MapPin,
  Phone,
  Mail,
  Globe,
  Facebook,
  Instagram,
  Linkedin,
  Mountain,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLocalizedUrl } from "@/utils/languageUtils";
import { trackContactAction } from "@/utils/contactTracking";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { language, setLanguage, t } = useLanguage();

  return (
    <footer
      className="relative z-30"
      style={{ backgroundColor: "#E4DFD6" }}
      data-oid="n:h.slh"
    >
      {/* Main Footer Content */}
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16"
        data-oid="dss3.5r"
      >
        <div
          className="grid lg:grid-cols-4 md:grid-cols-2 gap-8"
          data-oid="w0zsu_y"
        >
          {/* Hotel Information */}
          <div data-oid="-e55_o_">
            <div className="flex items-center mb-6" data-oid="7dcghya">
              <Mountain
                className="text-alpine-gold mr-2"
                size={28}
                data-oid="vx65kp5"
              />

              <h3
                className="font-playfair text-xl font-bold text-pine-green"
                data-oid="ucjopyh"
              >
                Beatenberg Resort & Spa
              </h3>
            </div>
            <p
              className="font-lato text-pine-green/70 mb-6 leading-relaxed"
              data-oid="9ornyjg"
            >
              {t("footer.hotelDescription")}
            </p>
            <div className="flex space-x-4" data-oid=".7:sahi">
              <a
                href="#"
                className="text-pine-green hover:text-alpine-gold transition-colors duration-300"
                data-oid="o3-zg3e"
              >
                <Facebook size={20} data-oid="wdg8kcy" />
              </a>
              <a
                href="#"
                className="text-pine-green hover:text-alpine-gold transition-colors duration-300"
                data-oid="ubm07b9"
              >
                <Instagram size={20} data-oid="h2rgsgc" />
              </a>
              <a
                href="#"
                className="text-pine-green hover:text-alpine-gold transition-colors duration-300"
                data-oid="ybh5u7i"
              >
                <Linkedin size={20} data-oid="fmf24ld" />
              </a>
            </div>
          </div>

          {/* Contact Information */}
          <div data-oid="6fz8fwg">
            <h4
              className="font-playfair text-lg font-semibold text-pine-green mb-6"
              data-oid="3tp.nvy"
            >
              {t("footer.contact")}
            </h4>
            <div className="space-y-4" data-oid="6-qd6_i">
              <div className="flex items-start space-x-3" data-oid="19x3aon">
                <MapPin
                  className="text-alpine-gold mt-1 flex-shrink-0"
                  size={16}
                  data-oid="4ty06jb"
                />

                <div
                  className="font-lato text-pine-green/70 text-sm leading-relaxed"
                  data-oid="4kakfjn"
                >
                  Spirenwaldstrasse 351
                  <br data-oid="myybovo" />
                  3803 Beatenberg
                  <br data-oid="sisjykf" />
                  Switzerland
                </div>
              </div>
              <div className="flex items-center space-x-3" data-oid="gp4q6z6">
                <Phone
                  className="text-alpine-gold flex-shrink-0"
                  size={16}
                  data-oid="f6m6wdp"
                />

                <a
                  href="tel:+41338410021"
                  onClick={() =>
                    trackContactAction("concierge", "phone", {
                      phone_number: "+41338410021",
                      contact_source: "footer_direct",
                      location: "footer",
                    })
                  }
                  className="contact-action-track font-lato text-pine-green/70 hover:text-alpine-gold text-sm transition-colors duration-200"
                  data-oid="t3h78o-"
                >
                  +41 33 841 00 21
                </a>
              </div>
              <div className="flex items-center space-x-3" data-oid="7cpifmx">
                <Mail
                  className="text-alpine-gold flex-shrink-0"
                  size={16}
                  data-oid=":n96l5b"
                />

                <a
                  href="mailto:info@beatenberg-resortspa.ch"
                  onClick={() =>
                    trackContactAction("concierge", "email", {
                      email_address: "info@beatenberg-resortspa.ch",
                      contact_source: "footer_direct",
                      location: "footer",
                    })
                  }
                  className="contact-action-track font-lato text-pine-green/70 hover:text-alpine-gold text-sm transition-colors duration-200"
                  data-oid="cc:m7jt"
                >
                  info@beatenberg-resortspa.ch
                </a>
              </div>
              <div className="flex items-center space-x-3" data-oid="ndppz-j">
                <Globe
                  className="text-alpine-gold flex-shrink-0"
                  size={16}
                  data-oid="h269igj"
                />

                <div className="flex space-x-3" data-oid="zjyn_u5">
                  <button
                    onClick={() => setLanguage("en")}
                    className={`font-lato text-sm transition-colors duration-300 ${
                      language === "en"
                        ? "text-alpine-gold font-semibold"
                        : "text-pine-green hover:text-alpine-gold"
                    }`}
                    data-oid="nm0-i:n"
                  >
                    EN
                  </button>
                  <button
                    onClick={() => setLanguage("de")}
                    className={`font-lato text-sm transition-colors duration-300 ${
                      language === "de"
                        ? "text-alpine-gold font-semibold"
                        : "text-pine-green hover:text-alpine-gold"
                    }`}
                    data-oid="lr6q.bm"
                  >
                    DE
                  </button>
                  <button
                    onClick={() => setLanguage("fr")}
                    className={`font-lato text-sm transition-colors duration-300 ${
                      language === "fr"
                        ? "text-alpine-gold font-semibold"
                        : "text-pine-green hover:text-alpine-gold"
                    }`}
                    data-oid="0hvw7h_"
                  >
                    FR
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Location */}
          <div data-oid="xq9ndxs">
            <h4
              className="font-playfair text-lg font-semibold text-pine-green mb-6"
              data-oid="zadlx8m"
            >
              {t("footer.location")}
            </h4>
            <div className="w-full" data-oid="._.pbxl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2736.408682929248!2d7.7945598!3d46.6976705!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478fa541a569a32d%3A0x786359f5bdf1a5e4!2sBeatenberg%20Resort%20%26%20Spa!5e0!3m2!1sen!2sin!4v1760786128427!5m2!1sen!2sin"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="rounded-lg"
                data-oid="gr8rkn5"
              />
            </div>
          </div>

          {/* Quick Links */}
          <div data-oid="mf79jeg">
            <h4
              className="font-playfair text-lg font-semibold text-pine-green mb-6"
              data-oid="ysj_ag5"
            >
              {t("footer.quickLinks")}
            </h4>
            <div className="space-y-3" data-oid="9bpxo3-">
              {[
                { name: t("footer.roomsSuites"), href: "/accommodations" },
                { name: t("footer.wellnessSpa"), href: "/spa" },
                { name: t("footer.dining"), href: "/dining" },
                { name: t("footer.experiences"), href: "/local-attractions" },
                { name: t("footer.eventsWeddings"), href: "/events" },
                { name: t("footer.aboutUs"), href: "/#about" },
                { name: t("footer.contactLink"), href: "/#contact" },
              ].map((link) => (
                <a
                  key={link.name}
                  href={getLocalizedUrl(link.href, language)}
                  className="block font-lato text-pine-green/70 hover:text-alpine-gold text-sm transition-colors duration-300 alpine-link"
                  data-oid="82bjt0n"
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Organization - Horizontal Grid */}
        <div
          className="mt-16 pt-8 border-t border-pine-green/20"
          data-oid="yue.o:_"
        >
          <h4
            className="font-playfair text-lg font-semibold text-pine-green mb-8 text-center"
            data-oid="_fz6tlq"
          >
            {t("footer.organization")}
          </h4>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6"
            data-oid="z_0hp7r"
          >
            {/* NutraSwiss */}
            <a
              href="https://www.nutraswiss.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center text-center group hover:opacity-80 transition-opacity duration-300"
              data-oid="239r:j2"
            >
              <img
                src="/images/NUTRASWISS-LOGO.png"
                alt="NutraSwiss"
                className="h-12 w-24 object-contain mb-3"
                data-oid="hq1ejn1"
              />

              <div
                className="font-lato text-pine-green text-sm font-medium group-hover:text-alpine-gold transition-colors duration-300"
                data-oid="oopuxn."
              >
                NutraSwiss
              </div>
              <div
                className="font-lato text-pine-green/60 text-xs mt-1"
                data-oid="xnpf0g0"
              >
                {t("footer.nutraSwissDescription")}
              </div>
            </a>

            {/* Clinique NutraSwiss */}
            <a
              href="https://www.cliniquenutraswiss.ch/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center text-center group hover:opacity-80 transition-opacity duration-300"
              data-oid="-ps0d_0"
            >
              <img
                src="/images/clinique_nutraswiss.avif"
                alt="Clinique NutraSwiss"
                className="h-12 w-24 object-contain mb-3"
                data-oid="cp23i4f"
              />

              <div
                className="font-lato text-pine-green text-sm font-medium group-hover:text-alpine-gold transition-colors duration-300"
                data-oid="u71t.ie"
              >
                Clinique NutraSwiss
              </div>
              <div
                className="font-lato text-pine-green/60 text-xs mt-1"
                data-oid="q6tbo.p"
              >
                {t("footer.cliniqueNutraSwissDescription")}
              </div>
            </a>

            {/* Nahrin */}
            <a
              href="https://www.nahrin.com/en"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center text-center group hover:opacity-80 transition-opacity duration-300"
              data-oid="rh_f3ur"
            >
              <img
                src="/images/nahrin.svg"
                alt="Nahrin"
                className="h-12 w-24 object-contain mb-3"
                data-oid="kfxjr.t"
              />

              <div
                className="font-lato text-pine-green text-sm font-medium group-hover:text-alpine-gold transition-colors duration-300"
                data-oid="dxl6ru5"
              >
                Nahrin
              </div>
              <div
                className="font-lato text-pine-green/60 text-xs mt-1"
                data-oid="k7bs8s1"
              >
                {t("footer.nahrinDescription")}
              </div>
            </a>

            {/* La Maison Pondicherry */}
            <a
              href="https://www.lamaisonpondichery.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center text-center group hover:opacity-80 transition-opacity duration-300"
              data-oid="wv89:7i"
            >
              <img
                src="/images/la maison pondicherry.png"
                alt="La Maison Pondicherry"
                className="h-12 w-24 object-contain mb-3"
                data-oid="46lhxrd"
              />

              <div
                className="font-lato text-pine-green text-sm font-medium group-hover:text-alpine-gold transition-colors duration-300"
                data-oid="vl7ar2-"
              >
                La Maison Pondichéry
              </div>
              <div
                className="font-lato text-pine-green/60 text-xs mt-1"
                data-oid="1ehigdi"
              >
                {t("footer.laMaisonPondicherryDescription")}
              </div>
            </a>

            {/* St. Beatus College */}
            <a
              href="https://www.st-beatus.college/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center text-center group hover:opacity-80 transition-opacity duration-300"
              data-oid="t0rsdz8"
            >
              <img
                src="/images/stbeatus.avif"
                alt="St. Beatus College"
                className="h-12 w-24 object-contain mb-3"
                data-oid="v.k._5b"
              />

              <div
                className="font-lato text-pine-green text-sm font-medium group-hover:text-alpine-gold transition-colors duration-300"
                data-oid="7b9f6a6"
              >
                St. Beatus College
              </div>
              <div
                className="font-lato text-pine-green/60 text-xs mt-1"
                data-oid="7gp89bl"
              >
                {t("footer.stBeatusCollegeDescription")}
              </div>
            </a>

            {/* Restaurant Jungfrau */}
            <a
              href={getLocalizedUrl("/dining", language)}
              className="flex flex-col items-center text-center group hover:opacity-80 transition-opacity duration-300"
              data-oid="2gpmp-q"
            >
              <img
                src="/images/jungfrau dark.webp"
                alt="Restaurant Jungfrau"
                className="h-12 w-24 object-contain mb-3"
                data-oid="4:v.9:3"
              />

              <div
                className="font-lato text-pine-green text-sm font-medium group-hover:text-alpine-gold transition-colors duration-300"
                data-oid="5:i:444"
              >
                Restaurant Jungfrau
              </div>
              <div
                className="font-lato text-pine-green/60 text-xs mt-1"
                data-oid="6jiw7-h"
              >
                {t("footer.restaurantJungfrauDescription")}
              </div>
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-pine-green/20" data-oid="-fs75e5">
        <div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6"
          data-oid="83k7np9"
        >
          <div
            className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0"
            data-oid="k0uvr5:"
          >
            <div
              className="font-lato text-pine-green/70 text-sm"
              data-oid="0qfc145"
            >
              © {currentYear} Beatenberg Resort & Spa |{" "}
              {t("footer.allRightsReserved")}
            </div>
            <div className="flex space-x-6" data-oid="gj_5o8u">
              <a
                href={getLocalizedUrl("/privacy-policy", language)}
                className="font-lato text-pine-green/70 hover:text-alpine-gold text-sm transition-colors duration-300"
                data-oid="iut3wgd"
              >
                {t("footer.privacyPolicy")}
              </a>
              <a
                href="#"
                className="font-lato text-pine-green/70 hover:text-alpine-gold text-sm transition-colors duration-300"
                data-oid="bvuew:t"
              >
                {t("footer.termsOfService")}
              </a>
              <a
                href="#"
                className="font-lato text-pine-green/70 hover:text-alpine-gold text-sm transition-colors duration-300"
                data-oid="dr9djdc"
              >
                {t("footer.cookies")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
